// 261010 P9D D5 (SC2 / SC3 / SC5) — công cụ cấp CẢNH ở bước 1: giờ trong ngày · khoá cảnh · nhân vật của cảnh.
// Giờ + khoá: route có sẵn (`PUT scenes/:id/time-of-day|lock`). Nhân vật cảnh: KHÔNG có route riêng ⇒ đọc plan MỚI, dựng ops bằng `sceneCastOpsOf` (thuần) rồi `batch-edit` MỘT lần
// (đúng đường lưu của UI cũ). Đổi dàn nhân vật = đổi shot ⇒ đổi hash Phân đoạn, phải duyệt lại.
import React, { useMemo, useState } from 'react';
import { Button, Popover, Select, Tooltip, message } from 'antd';
import WfIcon from './WfIcon';
import { filmApi } from '../../services/api/film';
import type { EventPlanScene } from '../../types/film';
import { apiErrorText } from './apiErrorText';
import { sceneCastOpsOf } from './workflowEditOps';
import { SCENE_TIME_OPTIONS, sceneTimeToastOf } from './workflowStructOps';

interface Props {
  planId: string;
  scene: EventPlanScene;
  castOptions: string[];
  onActionDone: () => void;
}

const castOf = (scene: EventPlanScene): string[] =>
  Array.from(new Set(scene.events.flatMap((e) => (e.shots || []).flatMap((s) => ((s.presence as { visible?: string[] } | null)?.visible || [])))));

const WorkflowSceneTools: React.FC<Props> = ({ planId, scene, castOptions, onActionDone }) => {
  const [busy, setBusy] = useState(false);
  const [castOpen, setCastOpen] = useState(false);
  const current = useMemo(() => castOf(scene), [scene]);
  const [picked, setPicked] = useState<string[]>(current);
  const resolved = scene.timeOfDayResolved;
  const timeLabel = resolved?.value ? SCENE_TIME_OPTIONS.find((o) => o.value === resolved.value)?.label ?? resolved.value : 'chưa rõ giờ';

  const wrap = async (label: string, fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); }
    catch (e) { message.error(apiErrorText(e, label)); }
    finally { setBusy(false); onActionDone(); }
  };
  const setTime = (v: string | undefined) => wrap('Đổi giờ trong ngày', async () => {
    const r = await filmApi.setSceneTimeOfDay(scene.id, v ?? null);
    message.success(sceneTimeToastOf(r.staleEvents));
  });
  const toggleLock = () => wrap(scene.isLocked ? 'Mở khoá cảnh' : 'Khoá cảnh', async () => {
    await filmApi.setSceneLock(scene.id, !scene.isLocked);
    message.success(scene.isLocked ? 'Đã mở khoá cảnh.' : 'Đã khoá cảnh — máy không tự sửa cảnh này.');
  });
  const saveCast = () => wrap('Đổi nhân vật của cảnh', async () => {
    const fresh = await filmApi.getEventPlan(planId); // plan MỚI: tránh ghi đè bản shot đã đổi ở nơi khác
    const sc = fresh.scenes.find((x) => x.id === scene.id);
    if (!sc) throw new Error('Cảnh không còn trong plan — tải lại trang.');
    const ops = sceneCastOpsOf(sc.events, picked);
    if (!ops.length) { message.info('Không có thay đổi nhân vật.'); setCastOpen(false); return; }
    await filmApi.batchEditPlan(planId, ops);
    message.success('Đã đổi nhân vật của cảnh — Phân đoạn đã đổi, phải duyệt lại.');
    setCastOpen(false);
  });

  return (
    <div className="wf-scene-tools" data-testid={`wf-scene-tools-${scene.id}`} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0' }}>
      <Popover trigger="click" placement="bottomLeft" title={<span style={{ fontSize: 12, fontWeight: 400 }}>Giờ trong ngày của cảnh — quyết định ánh sáng mọi clip trong cảnh. Bỏ chọn để lấy theo tiêu đề cảnh. Đổi xong phải dựng lại prompt.</span>}
        content={<Select size="small" style={{ width: 160 }} allowClear placeholder="Theo tiêu đề cảnh" value={scene.timeOfDay || undefined} options={SCENE_TIME_OPTIONS} disabled={busy} onChange={(v) => { void setTime(v); }} />}>
        <span className="wf-ib-w"><Tooltip title={`Giờ trong ngày: ${timeLabel}${resolved?.source === 'scene' ? ' · đã đè' : ''}`}>
          <Button size="small" type="text" disabled={busy} data-testid="wf-scene-time" aria-label={`Giờ trong ngày: ${timeLabel}`} icon={<WfIcon name="clock" />}>{resolved?.value ? <>{timeLabel}{resolved?.source === 'scene' ? <WfIcon name="edit" /> : null}</> : null}</Button>
        </Tooltip></span>
      </Popover>
      <Tooltip title={`${scene.isLocked ? 'Đã khoá — bấm để mở khoá cảnh' : 'Khoá cảnh'}. Cảnh khoá thì máy không sửa lại khi chạy lại bước.`}>
        <Button size="small" type={scene.isLocked ? 'default' : 'text'} disabled={busy} onClick={() => { void toggleLock(); }} data-testid="wf-scene-lock" aria-label={scene.isLocked ? 'Đã khoá' : 'Khoá cảnh'} aria-pressed={!!scene.isLocked} icon={<WfIcon name={scene.isLocked ? 'lock' : 'unlock'} />} />
      </Tooltip>
      <Popover open={castOpen} onOpenChange={(o) => { setCastOpen(o); if (o) setPicked(current); }} trigger="click" placement="bottomLeft"
        title={<span style={{ fontSize: 12, fontWeight: 400 }}>Nhân vật xuất hiện trong cảnh (áp cho mọi video của cảnh)</span>}
        content={(
          <div style={{ maxWidth: 320 }}>
            <Select mode="multiple" size="small" style={{ minWidth: 240 }} placeholder="Chọn nhân vật" value={picked} options={Array.from(new Set([...castOptions, ...current])).map((n) => ({ label: n, value: n }))} onChange={setPicked} disabled={busy} />
            <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
              <Button size="small" type="primary" loading={busy} onClick={() => { void saveCast(); }}>Lưu nhân vật</Button>
              <Button size="small" onClick={() => setCastOpen(false)}>Đóng</Button>
            </div>
            <p className="wf-note" style={{ margin: '6px 0 0' }}>Đổi nhân vật là đổi shot ⇒ đổi hash Phân đoạn, phải duyệt lại.</p>
          </div>
        )}>
        <span className="wf-ib-w"><Tooltip title={current.length ? `Nhân vật trong cảnh: ${current.join(', ')}` : 'Thêm nhân vật'}>
          <Button size="small" type="text" disabled={busy} data-testid="wf-scene-cast" aria-label={current.length ? `Nhân vật trong cảnh: ${current.join(', ')}` : 'Thêm nhân vật'} icon={<WfIcon name={current.length ? 'team' : 'userAdd'} />}>{current.length ? current.join(', ') : null}</Button>
        </Tooltip></span>
      </Popover>
    </div>
  );
};

export default WorkflowSceneTools;
